import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  InputNumber,
  Modal,
  Radio,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useCan } from '@refinedev/core';
import { adminApi } from '@/services/api';
import type { CardGenerationConfig } from '@/types';
import { RARITY_LABELS, rarityOddsProblem, rarityOddsTotal } from './cardRarityOdds';
import {
  CardRarityMode,
  DEFAULT_DIVISION_NAMES,
  DIVISION_TIERS,
  DivisionRarityOdds,
  divisionOddsForSave,
  divisionOddsFromServer,
  divisionOddsProblems,
  gddDivisionOdds,
  rarityModeFromServer,
  sameDivisionOdds,
} from './cardRarityDivisionOdds';

const { Title, Text } = Typography;

const RARITY_COLUMNS = [
  { rarity: '0', color: 'default' },
  { rarity: '1', color: 'blue' },
  { rarity: '2', color: 'purple' },
  { rarity: '3', color: 'gold' },
];

const MODE_LABELS: Record<CardRarityMode, string> = {
  global: 'Same odds in every division',
  division: 'By division (GDD table)',
};

/**
 * Config → Card Generation: whether a card made without a set rarity uses the global odds above
 * or the odds for the player's division (GDD v1.03 "Player Card Drop rate"), and that table.
 */
export const CardRarityDivisionSection: React.FC = () => {
  const { data: canEdit } = useCan({ resource: 'config', action: 'edit' });
  const [mode, setMode] = useState<CardRarityMode>('global');
  const [savedMode, setSavedMode] = useState<CardRarityMode>('global');
  const [table, setTable] = useState<DivisionRarityOdds>(gddDivisionOdds);
  const [savedTable, setSavedTable] = useState<DivisionRarityOdds>(gddDivisionOdds);
  const [names, setNames] = useState<Record<string, string>>(DEFAULT_DIVISION_NAMES);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const apply = useCallback((cfg: CardGenerationConfig) => {
    const m = rarityModeFromServer(cfg.cardRarityMode);
    const t = divisionOddsFromServer(cfg.cardRarityWeightsByDivision);
    setMode(m);
    setSavedMode(m);
    setTable(t);
    setSavedTable(divisionOddsFromServer(cfg.cardRarityWeightsByDivision));
    if (Array.isArray(cfg.divisions) && cfg.divisions.length > 0) {
      setNames({
        ...DEFAULT_DIVISION_NAMES,
        ...Object.fromEntries(cfg.divisions.map((d) => [String(d.tier), d.name])),
      });
    }
  }, []);

  const load = useCallback(async () => {
    try {
      const res = await adminApi.getCardGenerationConfig();
      const cfg = (res.data ?? {}) as CardGenerationConfig;
      apply(cfg);
      if (!cfg.cardRarityWeightsByDivision) {
        setLoadError(
          'This backend does not have division odds yet. Every division uses the odds above; the GDD table is shown for reference only.'
        );
        return;
      }
      setLoaded(true);
    } catch {
      setLoadError('Could not load the division odds.');
    }
  }, [apply]);

  useEffect(() => {
    void load();
  }, [load]);

  const editable = canEdit?.can !== false && loaded;
  const problems = useMemo(() => divisionOddsProblems(table), [table]);
  const changed = mode !== savedMode || !sameDivisionOdds(table, savedTable);
  const isGdd = sameDivisionOdds(table, gddDivisionOdds());

  const setCell = (tier: string, rarity: string, v: number | null) =>
    setTable((prev) => ({
      ...prev,
      [tier]: { ...prev[tier], [rarity]: typeof v === 'number' ? v : undefined },
    }));

  const handleSave = () => {
    if (problems.length > 0) {
      message.error(`Division ${problems[0].tier}: ${problems[0].problem}`);
      return;
    }
    const modeLine =
      mode === 'division'
        ? 'Cards rolled without a set rarity will use the odds for the player’s division.'
        : 'Cards rolled without a set rarity will use the same odds (above) in every division.';
    Modal.confirm({
      title: 'Change the card rarity odds by division?',
      icon: <ExclamationCircleOutlined />,
      content: `${MODE_LABELS[mode]}. ${modeLine} Every server uses this within seconds. Cards players already have are not changed.`,
      onOk: async () => {
        setSaving(true);
        try {
          const res = await adminApi.updateCardRarityDivision({
            cardRarityMode: mode,
            cardRarityWeightsByDivision: divisionOddsForSave(table),
          });
          const cfg = res.data as CardGenerationConfig | undefined;
          if (cfg?.cardRarityWeightsByDivision) {
            apply(cfg);
          } else {
            setSavedMode(mode);
            setSavedTable(table);
          }
          message.success('Card rarity odds by division updated');
        } catch (err: unknown) {
          const apiErr = err as { response?: { data?: { message?: string | string[] } } };
          const apiMsg = apiErr?.response?.data?.message;
          message.error(
            (Array.isArray(apiMsg) ? apiMsg.join(', ') : apiMsg) ||
              'Failed to update the card rarity odds by division'
          );
        } finally {
          setSaving(false);
        }
      },
    });
  };

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div>
        <Title level={5}>Card Rarity Odds by Division</Title>
        <Text type="secondary">
          Used for match-win drops, Quick Play stump cards and level-up player drops; packs, deals,
          Challenger and prizes keep their own rarity. With “By division” on, these replace the odds
          above for those cards, using the player’s current division (no division yet = Rookie
          League III). An Experiments v2 test on <Text code>card_rarity_odds</Text> can override the
          choice for its players. Each row must add up to 100%.
        </Text>
      </div>

      {loadError && <Alert type="warning" showIcon message={loadError} />}

      <Radio.Group
        value={mode}
        disabled={!editable}
        onChange={(e) => setMode(e.target.value as CardRarityMode)}
      >
        <Radio value="global">{MODE_LABELS.global}</Radio>
        <Radio value="division">{MODE_LABELS.division}</Radio>
      </Radio.Group>

      {mode === 'global' && loaded && (
        <Text type="secondary">
          The table below is kept but not used while “{MODE_LABELS.global}” is selected.
        </Text>
      )}

      <Table<{ tier: string }>
        rowKey="tier"
        pagination={false}
        size="small"
        scroll={{ x: 'max-content' }}
        dataSource={DIVISION_TIERS.map((tier) => ({ tier }))}
        columns={[
          { title: 'Division #', key: 'tier', width: 90, render: (_: unknown, row) => row.tier },
          {
            title: 'Name',
            key: 'name',
            width: 170,
            render: (_: unknown, row) => names[row.tier] ?? `Division ${row.tier}`,
          },
          ...RARITY_COLUMNS.map((c) => ({
            title: <Tag color={c.color}>{RARITY_LABELS[c.rarity]}</Tag>,
            key: `r${c.rarity}`,
            width: 110,
            render: (_: unknown, row: { tier: string }) => (
              <InputNumber
                min={0}
                max={100}
                step={0.5}
                precision={2}
                disabled={!editable}
                value={table[row.tier]?.[c.rarity]}
                onChange={(v) => setCell(row.tier, c.rarity, v)}
                style={{ width: 90 }}
              />
            ),
          })),
          {
            title: 'Total',
            key: 'total',
            width: 90,
            render: (_: unknown, row) => {
              const odds = table[row.tier] ?? {};
              const problem = rarityOddsProblem(odds);
              const text = (
                <Text strong type={problem ? 'danger' : undefined}>
                  {rarityOddsTotal(odds)}%
                </Text>
              );
              return problem ? <Tooltip title={problem}>{text}</Tooltip> : text;
            },
          },
        ]}
      />

      {problems.length > 0 && (
        <Alert
          type="error"
          showIcon
          message={`${problems.length} division row${problems.length === 1 ? '' : 's'} cannot be saved`}
          description={problems
            .map((p) => `Division ${p.tier} (${names[p.tier] ?? p.tier}): ${p.problem}`)
            .join(' ')}
        />
      )}

      <Space wrap>
        <Button
          type="primary"
          size="large"
          onClick={handleSave}
          loading={saving}
          disabled={!editable || problems.length > 0 || !changed}
        >
          Save Division Odds
        </Button>
        <Button onClick={() => setTable(gddDivisionOdds())} disabled={!editable || saving || isGdd}>
          Reset to GDD values
        </Button>
        {changed && editable && (
          <Button
            onClick={() => {
              setMode(savedMode);
              setTable(savedTable);
            }}
            disabled={saving}
          >
            Undo changes
          </Button>
        )}
      </Space>
    </Space>
  );
};
